import React, { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Badge from 'react-bootstrap/Badge'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { toast } from 'react-toastify'
import ActionButton from 'shared/buttons/action-button'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { errorMessage } from 'admin/shared/errors'
import {
  useAccountGroupings,
  useCreateAccountGrouping,
  useUpdateAccountGrouping,
  useDeleteAccountGrouping
} from 'api/hooks/use-account-groupings'

// Manages the taxonomies themselves — "OCAS", "ACFR" — as opposed to the groups inside one.
// A modal rather than a screen of its own: a district keeps two or three and edits them once a
// year, so this keeps the grouping and its contents visible together.
const GroupingModal = ({ show, onHide, onSelect }) => {
  const { data: groupings } = useAccountGroupings({})
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState({ name: '', description: '' })
  const [creating, setCreating] = useState({ name: '', description: '' })

  const onError = (fallback) => (err) => toast.error(errorMessage(err, fallback))

  const { mutate: createGrouping, isLoading: isCreating } = useCreateAccountGrouping({
    options: {
      onSuccess: (created) => {
        toast.success('Account grouping created')
        setCreating({ name: '', description: '' })
        // Land the page on the new grouping — it is empty, so the list behind the modal
        // would otherwise look unchanged.
        if (created?.id) onSelect?.(created.id)
      },
      onError: onError('Could not create the account grouping')
    }
  })

  const { mutate: updateGrouping } = useUpdateAccountGrouping({
    options: {
      onSuccess: () => {
        toast.success('Account grouping updated')
        setEditingId(null)
      },
      onError: onError('Could not update the account grouping')
    }
  })

  const { mutate: deleteGrouping } = useDeleteAccountGrouping({
    options: {
      onSuccess: () => toast.success('Account grouping deleted'),
      onError: onError('Could not delete the account grouping')
    }
  })

  const startEditing = (grouping) => {
    setEditingId(grouping.id)
    setDraft({ name: grouping.name, description: grouping.description || '' })
  }

  // Deleting a taxonomy deletes the rollups in it, and any View reporting under it falls back
  // to the default. Neither is visible from this list, so say so before the click.
  const handleDelete = (grouping) => {
    const groups = grouping.groups_count || 0
    const consequence = groups
      ? `Delete “${grouping.name}” and its ${groups} group${groups === 1 ? '' : 's'}? ` +
        'Those codes will report individually again, and any View reporting under ' +
        'this grouping falls back to the default one.'
      : `Delete “${grouping.name}”?`

    if (window.confirm(consequence)) deleteGrouping(grouping.id)
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Account Groupings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          A grouping is one way of reading the chart of accounts — the state’s
          OCAS series, your ACFR statement lines, whatever the board asks for.
          Each one rolls up the same codes differently, and a code can sit in one
          group in every grouping. A View reports under a single grouping, so its
          charts never mix two of them.
        </p>

        <Table hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Groups</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(groupings || []).map((grouping) => (
              <tr key={grouping.id}>
                {editingId === grouping.id ? (
                  <>
                    <td>
                      <Form.Control
                        value={draft.name}
                        aria-label={`Name for ${grouping.name}`}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        value={draft.description}
                        aria-label={`Description for ${grouping.name}`}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      />
                    </td>
                    <td>{grouping.groups_count}</td>
                    <td className="text-end text-nowrap">
                      <ActionButton
                        label="Save"
                        variant="primary"
                        className="me-2"
                        disabled={draft.name.trim().length === 0}
                        onClick={() => updateGrouping({ id: grouping.id, params: draft })}
                      />
                      <ActionButton
                        label="Cancel"
                        variant="light"
                        className="text-primary"
                        onClick={() => setEditingId(null)}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      {grouping.name}{' '}
                      {grouping.is_default && (
                        <Badge bg="light" text="primary">
                          Default
                        </Badge>
                      )}
                    </td>
                    <td className="text-muted small">{grouping.description}</td>
                    <td>{grouping.groups_count}</td>
                    <td className="text-end text-nowrap">
                      <ActionButton
                        label="Rename"
                        variant="light"
                        className="text-primary me-2"
                        onClick={() => startEditing(grouping)}
                      />
                      {!grouping.is_default && (
                        <ActionButton
                          label="Make default"
                          variant="light"
                          className="text-primary me-2"
                          onClick={() =>
                            updateGrouping({ id: grouping.id, params: { is_default: true } })
                          }
                        />
                      )}
                      {(groupings || []).length > 1 && (
                        <ActionButton
                          label="Delete"
                          variant="light"
                          className="text-danger"
                          onClick={() => handleDelete(grouping)}
                        />
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>

        <hr />

        <Row className="g-2 align-items-start">
          <Col md={4}>
            <Form.Control
              placeholder="ACFR"
              aria-label="New grouping name"
              value={creating.name}
              onChange={(e) => setCreating({ ...creating, name: e.target.value })}
            />
          </Col>
          <Col md={6}>
            <Form.Control
              placeholder="What this grouping is for"
              aria-label="New grouping description"
              value={creating.description}
              onChange={(e) => setCreating({ ...creating, description: e.target.value })}
            />
          </Col>
          <Col md={2}>
            <ActionButton
              label="Add"
              icon={faPlus}
              variant="primary"
              loading={isCreating}
              disabled={creating.name.trim().length === 0}
              onClick={() => createGrouping(creating)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <ActionButton label="Done" variant="light" className="text-primary" onClick={onHide} />
      </Modal.Footer>
    </Modal>
  )
}

export default GroupingModal
